// Client-side video compression — #135 PR B.
//
// ffmpeg-wasm pipeline that re-encodes uploaded videos to H.264 at 480p (CRF 28
// default) or 720p (CRF 22 high-fidelity). Compression happens in the browser
// BEFORE the upload reservation so reserve_workspace_upload sees the post-
// compression byte count and the 500MB workspace cap math holds.
//
// Bundle strategy:
//   The widget bundle (`w-1.0.0.js`) MUST stay ≤ 80 KB gzipped. ffmpeg-wasm's
//   JS wrapper is ~50 KB on its own and the WASM core is another ~30 MB raw.
//   This module dynamic-imports `@ffmpeg/ffmpeg` and `@ffmpeg/util` so Vite
//   emits them as a separate chunk under `dist/chunks/` — only fetched when
//   the user actually drags a video into the widget. The WASM core is loaded
//   from a public CDN at first use (single-threaded build, no SharedArrayBuffer
//   requirement). See coreURL / wasmURL options below.
//
// Threading model:
//   Single-threaded core only. The multi-threaded variant requires the host
//   page to set Cross-Origin-Opener-Policy: same-origin and Cross-Origin-
//   Embedder-Policy: require-corp on EVERY response, plus serve cross-origin
//   resources with CORP/CORS headers. Customer sites overwhelmingly do not
//   meet that contract — opting in to multi-threaded would break the widget
//   on most embeds. Single-threaded is ~2× slower but works without any host
//   CSP/COOP/COEP cooperation.
//
// CSP requirements for customer sites:
//   - script-src 'wasm-unsafe-eval'   — to instantiate the WASM module
//   - connect-src https://unpkg.com    — to fetch the WASM core (or whatever
//     CDN the customer points coreURL/wasmURL at)
//   The widget chunk file itself is served from widget.oncatch.app, which
//   customer's existing 'script-src https://widget.oncatch.app' (the embed
//   tag) already covers.
//
// Failure model:
//   Same as image compression (PR A): any unexpected error returns the
//   ORIGINAL blob with a `passthroughReason`. The reservation pipeline then
//   proceeds with raw bytes. We never block the user from submitting a bug
//   report because compression faltered — the layered cap defense
//   (#92 AC #7) already protects the workspace from oversized uploads at
//   the server side, and Sentry warnings on `ffmpeg_load_failed` give us
//   prod visibility into how often customer-site CSPs block the load.

import type { FFmpeg } from '@ffmpeg/ffmpeg';

export type VideoContentType = 'video/mp4' | 'video/webm';

export interface CompressVideoOptions {
  /**
   * x264 Constant Rate Factor. Lower = higher quality, larger output.
   * Default 28 (aggressive); pass 22 for `allow_high_fidelity_attachments` workspaces.
   */
  crf?: number;
  /**
   * Output max height in pixels. Aspect ratio preserved. Default 480.
   * Pass 720 for `allow_high_fidelity_attachments` workspaces.
   */
  maxHeight?: number;
  /** Skip compression entirely for inputs below this byte threshold. Default 1 MB. */
  skipBelowBytes?: number;
  /** Pluggable for tests. Defaults to dynamic import of @ffmpeg/ffmpeg + @ffmpeg/util. */
  loadFFmpeg?: () => Promise<FFmpegLoader>;
  /** CDN URL for ffmpeg-core.js. Default: unpkg.com pinned version. */
  coreURL?: string;
  /** CDN URL for ffmpeg-core.wasm. Default: unpkg.com pinned version. */
  wasmURL?: string;
  /**
   * Optional progress callback invoked while ffmpeg encodes. Receives a
   * progress fraction (0..1) plus the elapsed time in microseconds. Useful
   * for the widget UI to render a progress bar on long videos.
   */
  onProgress?: (progress: { fraction: number; timeMicros: number }) => void;
}

export interface FFmpegLoader {
  ffmpeg: FFmpeg;
  fetchFile: (blob: Blob) => Promise<Uint8Array>;
}

export interface CompressVideoResult {
  /** Compressed (or pass-through) blob. */
  blob: Blob;
  /** Final content type — 'video/mp4' on success or the input's type on fallback. */
  contentType: VideoContentType;
  /** Filename with extension swapped to .mp4 on success, or unchanged on fallback. */
  filename: string;
  /**
   * 'compressed' = output is a fresh ffmpeg re-encode.
   * 'passthrough' = output is the original blob (skipped or fell back).
   */
  outcome: 'compressed' | 'passthrough';
  /** Reason for passthrough, when outcome === 'passthrough'. Useful for telemetry. */
  passthroughReason?:
    | 'below_threshold'
    | 'not_a_video'
    | 'ffmpeg_load_failed'
    | 'compression_exec_failed'
    | 'compression_yielded_larger';
}

export const DEFAULT_CRF = 28;
export const DEFAULT_MAX_HEIGHT = 480;
export const DEFAULT_SKIP_BELOW_BYTES = 1 * 1024 * 1024;

// Pinned versions. Bumping requires re-testing the load path because the
// 0.12.x line has changed memory layout / API shape on minor versions
// in the past.
const FFMPEG_PKG_VERSION = '0.12.15';
const FFMPEG_UTIL_VERSION = '0.12.2';
const FFMPEG_CORE_VERSION = '0.12.10';

// Runtime URL imports rather than Vite-bundled dynamic imports. Two reasons:
//   1. Bundle hygiene — the widget's IIFE format inlines bundled dynamic
//      imports into the main file. Putting @ffmpeg/ffmpeg as a Vite-resolved
//      import would add ~3 KB gzipped to the always-loaded bundle even for
//      users who never drag a video. Runtime URL imports stay out entirely.
//   2. Worker URL resolution — @ffmpeg/ffmpeg's classes.js does
//      `new Worker(new URL('./worker.js', import.meta.url))`. Vite's IIFE
//      build emits a warning that `import.meta` will be replaced with `{}`,
//      which breaks worker resolution at runtime. Loading the package as a
//      real ESM module from a CDN (esm.sh) preserves import.meta.url and
//      the worker resolves correctly.
const DEFAULT_FFMPEG_URL = `https://esm.sh/@ffmpeg/ffmpeg@${FFMPEG_PKG_VERSION}`;
const DEFAULT_FFMPEG_UTIL_URL = `https://esm.sh/@ffmpeg/util@${FFMPEG_UTIL_VERSION}`;
const DEFAULT_CORE_URL = `https://unpkg.com/@ffmpeg/core@${FFMPEG_CORE_VERSION}/dist/umd/ffmpeg-core.js`;
const DEFAULT_WASM_URL = `https://unpkg.com/@ffmpeg/core@${FFMPEG_CORE_VERSION}/dist/umd/ffmpeg-core.wasm`;

const SUPPORTED_INPUT_TYPES: ReadonlyArray<string> = ['video/mp4', 'video/webm'];

/**
 * Default loader: runtime URL import of @ffmpeg/ffmpeg + @ffmpeg/util from
 * esm.sh so Vite doesn't inline them into the main bundle and so the
 * package's `new Worker(new URL('./worker.js', import.meta.url))` pattern
 * resolves against the CDN URL rather than against the IIFE's empty
 * import.meta. Customer CSP requirements are documented in the file header.
 */
async function defaultLoadFFmpeg(): Promise<FFmpegLoader> {
  const [ffmpegMod, utilMod] = await Promise.all([
    // @vite-ignore tells Vite NOT to try to resolve / bundle these — they
    // load at runtime from the CDN.
    import(/* @vite-ignore */ DEFAULT_FFMPEG_URL),
    import(/* @vite-ignore */ DEFAULT_FFMPEG_UTIL_URL),
  ]);
  return {
    ffmpeg: new ffmpegMod.FFmpeg(),
    fetchFile: utilMod.fetchFile,
  };
}

/**
 * Compress a video blob via ffmpeg-wasm → H.264. Falls back to the original
 * blob on any failure (load failed, exec error, output larger than input)
 * so the upload pipeline never breaks. The layered cap defense at the
 * server enforces hard size caps regardless of compression outcome.
 */
export async function compressVideo(
  input: { blob: Blob; contentType: VideoContentType; filename: string },
  options: CompressVideoOptions = {},
): Promise<CompressVideoResult> {
  const crf = options.crf ?? DEFAULT_CRF;
  const maxHeight = options.maxHeight ?? DEFAULT_MAX_HEIGHT;
  const skipBelow = options.skipBelowBytes ?? DEFAULT_SKIP_BELOW_BYTES;
  const coreURL = options.coreURL ?? DEFAULT_CORE_URL;
  const wasmURL = options.wasmURL ?? DEFAULT_WASM_URL;
  const loadFFmpeg = options.loadFFmpeg ?? defaultLoadFFmpeg;

  if (!SUPPORTED_INPUT_TYPES.includes(input.contentType)) {
    return passthrough(input, 'not_a_video');
  }

  if (input.blob.size <= skipBelow) {
    return passthrough(input, 'below_threshold');
  }

  let loader: FFmpegLoader;
  try {
    loader = await loadFFmpeg();
    await loader.ffmpeg.load({ coreURL, wasmURL });
  } catch {
    // CSP block, network failure, customer browser without WebAssembly,
    // pinned core unavailable — all funnel here. Emit a passthrough; the
    // reservation pipeline will still run with raw bytes. Sentry warning
    // is fired by the caller (AttachmentManager) so we have prod visibility
    // without coupling this module to Sentry.
    return passthrough(input, 'ffmpeg_load_failed');
  }

  const inputName = 'in';
  const outputName = 'out.mp4';

  // ffmpeg arg construction:
  //   -i <input>                  source
  //   -vf scale='-2:min(<H>, ih)' resize preserving aspect; -2 keeps width
  //                               divisible by 2 (required by H.264);
  //                               cap at maxHeight but never UPSCALE so a
  //                               360p input doesn't get blown up to 480p
  //   -c:v libx264 -preset fast   x264 encoder, fast preset
  //   -crf <CRF>                  rate control
  //   -c:a aac -b:a 128k          audio re-encode (sane bitrate)
  //   -movflags +faststart        moov atom at file head (streams faster)
  const args = [
    '-i',
    inputName,
    '-vf',
    `scale='-2:min(${maxHeight},ih)'`,
    '-c:v',
    'libx264',
    '-preset',
    'fast',
    '-crf',
    String(crf),
    '-c:a',
    'aac',
    '-b:a',
    '128k',
    '-movflags',
    '+faststart',
    outputName,
  ];

  if (options.onProgress) {
    try {
      loader.ffmpeg.on('progress', (event: { progress: number; time?: number }) => {
        options.onProgress?.({
          fraction: clamp01(event.progress),
          timeMicros: event.time ?? 0,
        });
      });
    } catch {
      // ffmpeg.on may throw if the loaded core doesn't expose progress
      // events. Non-fatal — encoding still proceeds.
    }
  }

  let outputData: Uint8Array;
  try {
    const inputData = await loader.fetchFile(input.blob);
    await loader.ffmpeg.writeFile(inputName, inputData);
    await loader.ffmpeg.exec(args);
    const result = await loader.ffmpeg.readFile(outputName);
    if (typeof result === 'string') {
      // ffmpeg-wasm returns string only when caller passed an encoding hint;
      // we never do, so this branch shouldn't fire. Defensive passthrough.
      return passthrough(input, 'compression_exec_failed');
    }
    outputData = result;
  } catch {
    return passthrough(input, 'compression_exec_failed');
  } finally {
    // Best-effort cleanup. terminate() destroys the worker so a single
    // long-lived ffmpeg instance doesn't accumulate over multiple uploads.
    try {
      loader.ffmpeg.terminate();
    } catch {
      // Cleanup failure is benign.
    }
  }

  const compressed = new Blob([new Uint8Array(outputData)], { type: 'video/mp4' });

  if (compressed.size >= input.blob.size) {
    // Encoder produced a file at least as large as the original (rare —
    // happens when the source is already aggressively compressed). Keep
    // the original to avoid wasting cap bytes on a worse re-encode.
    return passthrough(input, 'compression_yielded_larger');
  }

  return {
    blob: compressed,
    contentType: 'video/mp4',
    filename: rewriteExtension(input.filename, '.mp4'),
    outcome: 'compressed',
  };
}

function passthrough(
  input: { blob: Blob; contentType: VideoContentType; filename: string },
  reason: NonNullable<CompressVideoResult['passthroughReason']>,
): CompressVideoResult {
  return {
    blob: input.blob,
    contentType: input.contentType,
    filename: input.filename,
    outcome: 'passthrough',
    passthroughReason: reason,
  };
}

function rewriteExtension(filename: string, ext: string): string {
  const stripped = filename.replace(/\.[^./\\]+$/i, '');
  return `${stripped}${ext}`;
}

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}
